// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useBlocker } from '@tanstack/react-router';
import { useEffect } from 'react';

interface Props {
  // Whether a resync is currently running (the only time leaving should be guarded).
  active: boolean;
}

/**
 * Warns the user that leaving the page stops an in-progress resync, both for in-app navigation
 * (TanStack Router) and browser unload. Renders nothing unless a navigation is blocked.
 */
export function ResyncLeaveGuard(props: Props) {
  useEffect(() => {
    if (!props.active) {
      return;
    }
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [props.active]);

  const blocker = useBlocker({ condition: props.active });
  const isBlocked = blocker.status === 'blocked';

  return (
    <Dialog fullWidth maxWidth="xs" open={isBlocked}>
      <DialogTitle>Resync in progress</DialogTitle>
      <DialogContent dividers>
        <Typography>
          Leaving this page will stop the resync. Are you sure you want to
          leave?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={blocker.reset}>No, stay on this page</Button>
        <Button color="error" onClick={blocker.proceed}>
          Yes, leave and stop
        </Button>
      </DialogActions>
    </Dialog>
  );
}
